// =============================================================================
// MeuDriver — useCancellation (Batch J4)
// =============================================================================
// Hooks para política de cancelamento e reembolso automático.
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import * as Sentry from '@sentry/react'
import { supabase } from '@/lib/supabase'
import { bookingKeys } from '@/hooks/useBookings'
import { useAuth } from '@/hooks/useAuth'

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

export interface CancellationPreview {
  refund_percentage: number
  refund_amount: number
  hours_until: number
  policy_name: string
  /** Aula paga com crédito de pacote: nunca há dinheiro a devolver. */
  is_package?: boolean
  /** Valor da aula travado no saldo da carteira (0 = não pago com saldo). */
  wallet_amount?: number
  /** Pago com saldo ou pacote e dentro do prazo: o valor/crédito volta. */
  credit_returned?: boolean
}

export interface CancellationResult {
  success: boolean
  cancellation_log_id: string
  refund_percentage: number
  refund_amount: number
  new_status: string
  /** Saldo ou crédito de pacote voltou ao aluno. */
  credit_returned?: boolean
  /** Valor devolvido ao saldo da carteira. */
  wallet_returned?: number
  /**
   * `true` quando o cancelamento foi registrado mas o estorno no Mercado Pago
   * não pôde ser disparado. A UI deve avisar o usuário de que o valor será
   * devolvido manualmente pelo suporte, em vez de prometer o reembolso.
   */
  refund_failed?: boolean
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const cancellationKeys = {
  preview: (bookingId: string) => ['cancellation', 'preview', bookingId] as const,
}

// ---------------------------------------------------------------------------
// Hooks públicos
// ---------------------------------------------------------------------------

/**
 * Calcula o preview de reembolso para um booking específico.
 * Mostra ao usuário antes de confirmar o cancelamento.
 */
export function useCancellationPreview(bookingId: string) {
  return useQuery<CancellationPreview, Error>({
    queryKey: cancellationKeys.preview(bookingId),
    queryFn: async () => {
      const { data, error } = await supabase.rpc('calculate_refund_amount', {
        p_booking_id: bookingId,
      })

      if (error) throw new Error(error.message)

      return data as unknown as CancellationPreview
    },
    enabled: Boolean(bookingId),
    staleTime: 1000 * 60, // cache de 1 min — horas_até muda com o tempo
  })
}

/**
 * Cancela um booking aplicando a política de reembolso.
 * Se refund_amount > 0, chama a edge function admin-refund para processar
 * o reembolso no Mercado Pago.
 */
export function useCancelBooking() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation<CancellationResult, Error, {
    bookingId: string
    reason?: string
    cancelledBy: 'student' | 'instructor'
  }>({
    mutationFn: async ({ bookingId, reason, cancelledBy }) => {
      // 1. Processa cancelamento + calcula reembolso
      const { data, error } = await supabase.rpc('process_cancellation', {
        p_booking_id:   bookingId,
        p_reason:       reason ?? '',
        p_cancelled_by: cancelledBy,
      })

      if (error) throw new Error(error.message)

      const result = data as unknown as CancellationResult

      // 2. Dispara reembolso no MP se houver valor a reembolsar.
      //
      // O cancelamento em si já está persistido — não desfazemos por falha de
      // reembolso. Mas o erro NÃO pode ser silencioso: antes, um 403 do
      // admin-refund era engolido e o aluno via "cancelado, reembolso de R$X"
      // sem que um centavo voltasse. Agora sinalizamos via `refund_failed`
      // para a UI avisar que o estorno ficará para o suporte.
      //
      // O valor é recalculado no servidor a partir de `cancellation_log`;
      // enviamos `amount` apenas para o caso admin (onde é respeitado).
      if (result.refund_amount > 0) {
        const { error: refundError } = await supabase.functions.invoke('admin-refund', {
          body: {
            booking_id: bookingId,
            amount: result.refund_amount,
            reason: reason ?? 'Cancelamento conforme política',
          },
        })

        if (refundError) {
          if (import.meta.env.DEV) {
            console.error('[useCancelBooking] admin-refund falhou:', refundError.message)
          }
          Sentry.captureException(refundError, {
            tags: { source: 'useCancelBooking.admin-refund' },
            extra: { bookingId, refundAmount: result.refund_amount },
          })
          return { ...result, refund_failed: true }
        }
      }

      return result
    },
    onSuccess: (_data, { bookingId }) => {
      queryClient.invalidateQueries({ queryKey: bookingKeys.detail(bookingId) })
      queryClient.invalidateQueries({ queryKey: ['wallet'] })
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: bookingKeys.student(user.id) })
        queryClient.invalidateQueries({ queryKey: bookingKeys.instructor(user.id) })
      }
      queryClient.invalidateQueries({ queryKey: cancellationKeys.preview(bookingId) })
    },
  })
}
